/**
 * Phase 7+++ — duplicate Chef's Special detection ACROSS the entire voyage.
 *
 * Originally the duplicate highlight fired only when two restaurants on the
 * SAME day picked the same Chef's Special. This module extends the scope
 * to the whole voyage: a chef's-special name flagged red whenever it
 * appears 2+ times total across ALL regular restaurants × ALL days.
 *
 * Cases covered:
 *   - Same restaurant, two different days (e.g. MDR on Day 3 + Day 7)
 *   - Two restaurants, same day  (legacy — kept by the same rule)
 *   - Two restaurants, two days  (new in 7+++)
 *
 * Empty names + disabled toggle entries are skipped so a sea-day with no
 * chef's special never collides.
 *
 * Mirrors the architecture of `menuDuplicates.js`. Comparison is
 * case-insensitive + trimmed.
 *
 * Return shape:
 *   Map<normalisedName, number[]>   // sorted day-number list, length >= 2
 */
function _norm(name) {
  return (name || "").trim().toLowerCase();
}

export function computeChefSpecialDuplicates(allDays) {
  // normName → [day_numbers...]
  const counts = new Map();
  if (!Array.isArray(allDays)) return new Map();

  for (const day of allDays) {
    const dayNum = day?.day_number;
    if (!dayNum) continue;
    const rows = (day.meal_plan || {}).regular_restaurants || [];
    for (const row of rows) {
      const cs = row?.chefs_special;
      if (!cs?.enabled) continue;
      const key = _norm(cs.name);
      if (!key) continue;
      if (!counts.has(key)) counts.set(key, []);
      counts.get(key).push(dayNum);
    }
  }

  const out = new Map();
  for (const [key, days] of counts.entries()) {
    if (days.length >= 2) {
      // Stable, sorted day list for the tooltip text.
      out.set(key, [...days].sort((a, b) => a - b));
    }
  }
  return out;
}

/**
 * Given the duplicate map + a single day's regular-restaurant row + the
 * current day number, returns either:
 *   - { isDup: false, otherDays: null }
 *   - { isDup: true,  otherDays: [number, ...] }  // includes current day
 *                                                  // when the SAME chef's
 *                                                  // special appears 2+
 *                                                  // times on this day
 *
 * `otherDays` lists all OTHER day numbers (excluding the current day) that
 * also use this same name. The frontend builds a tooltip combining a
 * "same-day" note (when the count on the current day is >= 2) with a
 * "cross-day" note listing the other days.
 */
export function isRowChefSpecialDuplicate(map, row, currentDayNumber) {
  if (!map || !row) return { isDup: false, otherDays: null, sameDayCount: 0 };
  const cs = row?.chefs_special;
  if (!cs?.enabled) return { isDup: false, otherDays: null, sameDayCount: 0 };
  const key = _norm(cs.name);
  if (!key) return { isDup: false, otherDays: null, sameDayCount: 0 };
  const days = map.get(key);
  if (!days || days.length < 2)
    return { isDup: false, otherDays: null, sameDayCount: 0 };
  const sameDayCount = days.filter((n) => n === currentDayNumber).length;
  const otherDays = [...new Set(days.filter((n) => n !== currentDayNumber))];
  return { isDup: true, otherDays, sameDayCount };
}

/** Set of day numbers that contain at least one duplicate chef's special. */
export function daysWithDuplicateChefSpecials(map) {
  const out = new Set();
  if (!map) return out;
  for (const days of map.values()) {
    days.forEach((n) => out.add(n));
  }
  return out;
}
